<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  fetchAppVersion,
  refreshPageAndClearCache,
  shouldPromptVersionUpdate,
} from '../lib/app-version'

const CHECK_MS = 60 * 1000

const { t } = useI18n({ useScope: 'global' })
const outdated = ref(false)
const displayVersion = ref('')
const busy = ref(false)

let seenTag = ''
let timer: ReturnType<typeof setInterval> | null = null
let checking = false

async function checkVersion() {
  if (checking || import.meta.env.DEV || outdated.value) return
  checking = true
  try {
    const next = await fetchAppVersion()
    const result = shouldPromptVersionUpdate(seenTag, next)
    seenTag = result.seen
    if (result.prompt && next) {
      displayVersion.value = next.label
      outdated.value = true
      stop()
    }
  } catch {
    /* 版本文件暂时读不到时不打断兑换 */
  } finally {
    checking = false
  }
}

function start() {
  stop()
  timer = setInterval(() => {
    void checkVersion()
  }, CHECK_MS)
}

function stop() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

function onVisibility() {
  if (document.hidden) {
    stop()
    return
  }
  void checkVersion().finally(start)
}

async function onRefresh() {
  if (busy.value) return
  busy.value = true
  await refreshPageAndClearCache()
}

onMounted(() => {
  void checkVersion().finally(start)
  document.addEventListener('visibilitychange', onVisibility)
})

onUnmounted(() => {
  stop()
  document.removeEventListener('visibilitychange', onVisibility)
})
</script>

<template>
  <aside v-if="outdated" class="version-banner" role="status">
    <div class="version-banner__copy">
      <strong>{{ t('common.updateTitle') }}</strong>
      <p>{{ t('common.updateBody', { version: displayVersion || '—' }) }}</p>
    </div>
    <button type="button" class="version-banner__btn" :disabled="busy" @click="onRefresh">
      {{ busy ? t('common.loading') : t('common.updateRefresh') }}
    </button>
  </aside>
</template>

<style scoped>
.version-banner {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  padding: 12px 16px;
  border-bottom: 1px solid color-mix(in srgb, var(--warn, #d97706) 35%, var(--brd, #e5e7eb));
  background: color-mix(in srgb, var(--warn, #d97706) 12%, var(--soft, #fff7ed));
  color: var(--ink, #111827);
}
.version-banner__copy {
  min-width: 0;
  flex: 1 1 220px;
}
.version-banner__copy strong {
  display: block;
  font-size: 14px;
  line-height: 1.4;
}
.version-banner__copy p {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ink-2, #4b5563);
}
.version-banner__btn {
  flex: 0 0 auto;
  min-height: 44px;
  min-width: 140px;
  padding: 0 16px;
  border: 0;
  border-radius: 12px;
  background: var(--warn, #d97706);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.version-banner__btn:disabled {
  opacity: 0.7;
  cursor: wait;
}
</style>
